/**
 * HierarchicalTreeGraph.tsx - Hierarchical tree layout using D3
 * 
 * CHANGE HISTORY:
 * - 251031_2050 [pending] danielbeer: Initial creation
 * 
 * Tree-based layout showing parent-child relationships.
 * Best for: Understanding hierarchy, dependency chains, organizational structures
 */

import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { GraphNode, GraphEdge, GraphOptions, GraphCallbacks, GraphStyle, TreeLayoutOptions } from './types';

export interface HierarchicalTreeGraphProps<T = any> extends GraphOptions, GraphCallbacks<T> {
  data: {
    nodes: GraphNode<T>[];
    edges: GraphEdge[];
  };
  style?: GraphStyle;
  treeOptions?: TreeLayoutOptions;
  className?: string;
}

/**
 * HierarchicalTreeGraph Component
 * 
 * Features:
 * - Top-down or left-right tree layout
 * - Collapsible branches
 * - Clear parent-child relationships
 * - Automatic level spacing
 */
export const HierarchicalTreeGraph = <T = any,>({
  data,
  width = 1200,
  height = 800,
  style = {},
  treeOptions = {},
  className = '',
}: HierarchicalTreeGraphProps<T>) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || data.nodes.length === 0) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);
    const { orientation = 'vertical', nodeSpacing = 150, levelSpacing = 200 } = treeOptions;

    // Build hierarchy from edges
    const nodeMap = new Map(data.nodes.map(n => [n.id, { ...n, children: [] as any[] }]));
    const roots: any[] = [];
    const childrenSet = new Set<string>();

    // Identify parent-child relationships
    data.edges.forEach(edge => {
      const parent = nodeMap.get(edge.source);
      const child = nodeMap.get(edge.target);
      if (parent && child) {
        parent.children.push(child);
        childrenSet.add(edge.target);
      }
    });

    // Find root nodes (nodes with no incoming edges)
    data.nodes.forEach(node => {
      if (!childrenSet.has(node.id)) {
        const rootNode = nodeMap.get(node.id);
        if (rootNode) roots.push(rootNode);
      }
    });

    // If no roots found, use first node
    if (roots.length === 0 && data.nodes.length > 0) {
      const firstNode = nodeMap.get(data.nodes[0].id);
      if (firstNode) roots.push(firstNode);
    }

    // Create tree layout
    const treeLayout = d3.tree()
      .nodeSize(orientation === 'vertical' ? [nodeSpacing, levelSpacing] : [levelSpacing, nodeSpacing]);

    // Create container with appropriate transform
    const margin = { top: 50, right: 50, bottom: 50, left: 50 };
    const container = svg.append('g')
      .attr('transform', `translate(${orientation === 'vertical' ? width / 2 : margin.left},${orientation === 'vertical' ? margin.top : height / 2})`);

    // Enable zoom
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        container.attr('transform', 
          `translate(${orientation === 'vertical' ? width / 2 : margin.left},${orientation === 'vertical' ? margin.top : height / 2}) ${event.transform}`
        );
      });
    svg.call(zoom);

    // Render each root tree
    roots.forEach((root, index) => {
      const hierarchy = d3.hierarchy(root);
      const treeData = treeLayout(hierarchy);

      // Offset multiple trees
      const offsetX = index * 400;

      // Draw links
      container.selectAll(`.link-${index}`)
        .data(treeData.links())
        .join('path')
        .attr('class', `link-${index}`)
        .attr('d', (d: any) => {
          if (orientation === 'vertical') {
            return `M${d.source.x + offsetX},${d.source.y}
                    C${d.source.x + offsetX},${(d.source.y + d.target.y) / 2}
                     ${d.target.x + offsetX},${(d.source.y + d.target.y) / 2}
                     ${d.target.x + offsetX},${d.target.y}`;
          } else {
            return `M${d.source.y},${d.source.x + offsetX}
                    C${(d.source.y + d.target.y) / 2},${d.source.x + offsetX}
                     ${(d.source.y + d.target.y) / 2},${d.target.x + offsetX}
                     ${d.target.y},${d.target.x + offsetX}`;
          }
        })
        .attr('fill', 'none')
        .attr('stroke', typeof style.edgeColor === 'string' ? style.edgeColor : '#999')
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 0.6);

      // Draw nodes
      const nodes = container.selectAll(`.node-${index}`)
        .data(treeData.descendants())
        .join('g')
        .attr('class', `node-${index}`)
        .attr('transform', (d: any) => 
          orientation === 'vertical' 
            ? `translate(${d.x + offsetX},${d.y})`
            : `translate(${d.y},${d.x + offsetX})`
        );

      nodes.append('circle')
        .attr('r', (d: any) => 
          typeof style.nodeSize === 'function' ? style.nodeSize(d.data) :
          d.data.size || style.nodeSize || 20
        )
        .attr('fill', (d: any) => 
          typeof style.nodeColor === 'function' ? style.nodeColor(d.data) :
          d.data.color || style.nodeColor || '#3b82f6'
        )
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);

      nodes.append('text')
        .text((d: any) => d.data.label)
        .attr('text-anchor', 'middle')
        .attr('dy', (d: any) => {
          const radius = typeof style.nodeSize === 'function' ? style.nodeSize(d.data) :
            d.data.size || style.nodeSize || 20;
          return radius + 15;
        })
        .attr('font-size', style.fontSize || '12px')
        .attr('font-family', style.fontFamily || 'sans-serif')
        .attr('fill', '#333');
    });

  }, [data, width, height, style, treeOptions]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={`hierarchical-tree-graph ${className}`}
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        background: style.backgroundColor || '#fafafa',
      }}
    />
  );
};

export default HierarchicalTreeGraph;
